import React, { FormEvent, useEffect, useState } from 'react';
import {
  adminUserService,
  AdminUserListItem,
  CreateAdminUserRequest,
} from '../../../services/adminUserService';

const PAGE_LIMIT = 20;

const COGNITO_GROUP_OPTIONS = ['', 'ADMIN'] as const;

type Feedback = {
  type: 'success' | 'error';
  message: string;
};

const AdminUsersPage: React.FC = () => {
  const [usuarios, setUsuarios] = useState<AdminUserListItem[]>([]);
  const [nextToken, setNextToken] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [isCreatingUser, setIsCreatingUser] = useState(false);
  const [feedback, setFeedback] = useState<Feedback | null>(null);

  const [form, setForm] = useState<CreateAdminUserRequest>({
    email: '',
    temporaryPassword: '',
    name: '',
    phoneNumber: '',
    cpf: '',
    grupoCognito: '',
  });

  const loadUsers = async (append = false, token?: string) => {
    setIsLoadingUsers(true);
    setFeedback(null);
    try {
      const response = await adminUserService.list({
        limit: PAGE_LIMIT,
        nextToken: token,
      });

      setUsuarios((current) =>
        append ? [...current, ...(response.usuarios || [])] : response.usuarios || []
      );
      setNextToken(response.nextToken || null);
      setHasMore(Boolean(response.hasMore));
    } catch (error: any) {
      const status = error?.response?.status;
      if (status === 403) {
        setFeedback({ type: 'error', message: 'Você não possui permissão ADMIN para listar usuários.' });
      } else if (status === 401) {
        setFeedback({ type: 'error', message: 'Sessão inválida. Faça login novamente.' });
      } else {
        setFeedback({ type: 'error', message: 'Não foi possível carregar os usuários.' });
      }
    } finally {
      setIsLoadingUsers(false);
    }
  };

  useEffect(() => {
    loadUsers();
  }, []);

  const handleCreateUser = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setFeedback(null);

    const payload: CreateAdminUserRequest = {
      email: form.email.trim(),
      temporaryPassword: form.temporaryPassword.trim(),
      name: form.name?.trim() || undefined,
      phoneNumber: form.phoneNumber?.trim() || undefined,
      cpf: form.cpf?.trim() || undefined,
      grupoCognito: form.grupoCognito?.trim() || undefined,
    };

    if (!payload.email || !payload.temporaryPassword) {
      setFeedback({ type: 'error', message: 'Email e senha temporária são obrigatórios.' });
      return;
    }

    setIsCreatingUser(true);
    try {
      await adminUserService.create(payload);
      setFeedback({ type: 'success', message: 'Usuário criado com sucesso.' });
      setForm({
        email: '',
        temporaryPassword: '',
        name: '',
        phoneNumber: '',
        cpf: '',
        grupoCognito: '',
      });
      await loadUsers(false);
    } catch (error: any) {
      const status = error?.response?.status;
      if (status === 400) {
        setFeedback({ type: 'error', message: 'Payload inválido. Revise os dados preenchidos.' });
      } else if (status === 403) {
        setFeedback({ type: 'error', message: 'Você não possui permissão ADMIN para criar usuários.' });
      } else if (status === 401) {
        setFeedback({ type: 'error', message: 'Sessão inválida. Faça login novamente.' });
      } else {
        setFeedback({ type: 'error', message: 'Erro ao criar usuário. Tente novamente.' });
      }
    } finally {
      setIsCreatingUser(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-6 md:px-6 md:py-8">
      <div className="mb-6">
        <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Administração de Usuários</h1>
        <p className="text-gray-600 mt-1">
          Crie usuários no Cognito e acompanhe a lista de usuários existentes.
        </p>
      </div>

      {feedback && (
        <div
          className={`mb-4 rounded-lg border px-4 py-3 text-sm ${
            feedback.type === 'success'
              ? 'border-green-200 bg-green-50 text-green-700'
              : 'border-red-200 bg-red-50 text-red-700'
          }`}
        >
          {feedback.message}
        </div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-[380px_minmax(0,1fr)] gap-5">
        <section className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm h-fit">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Criar usuário</h2>
          <form onSubmit={handleCreateUser} className="space-y-3">
            <div>
              <label className="text-xs font-semibold text-gray-600">Email*</label>
              <input
                type="email"
                value={form.email}
                onChange={(event) => setForm((prev) => ({ ...prev, email: event.target.value }))}
                className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="usuario@empresa.com"
                required
              />
            </div>

            <div>
              <label className="text-xs font-semibold text-gray-600">Senha temporária*</label>
              <input
                type="text"
                value={form.temporaryPassword}
                onChange={(event) =>
                  setForm((prev) => ({ ...prev, temporaryPassword: event.target.value }))
                }
                className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="SenhaTemporaria#123"
                required
              />
            </div>

            <div>
              <label className="text-xs font-semibold text-gray-600">Nome</label>
              <input
                type="text"
                value={form.name}
                onChange={(event) => setForm((prev) => ({ ...prev, name: event.target.value }))}
                className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="text-xs font-semibold text-gray-600">Telefone (E.164)</label>
              <input
                type="text"
                value={form.phoneNumber}
                onChange={(event) =>
                  setForm((prev) => ({ ...prev, phoneNumber: event.target.value }))
                }
                className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="+5511999999999"
              />
            </div>

            <div>
              <label className="text-xs font-semibold text-gray-600">CPF</label>
              <input
                type="text"
                value={form.cpf}
                onChange={(event) => setForm((prev) => ({ ...prev, cpf: event.target.value }))}
                className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="123.456.789-00"
              />
            </div>

            <div>
              <label className="text-xs font-semibold text-gray-600">Grupo Cognito</label>
              <select
                value={form.grupoCognito || ''}
                onChange={(event) =>
                  setForm((prev) => ({ ...prev, grupoCognito: event.target.value }))
                }
                className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Sem grupo</option>
                {COGNITO_GROUP_OPTIONS.filter(Boolean).map((group) => (
                  <option key={group} value={group}>
                    {group}
                  </option>
                ))}
              </select>
            </div>

            <button
              type="submit"
              disabled={isCreatingUser}
              className="w-full rounded-lg bg-blue-700 px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-800 disabled:opacity-60"
            >
              {isCreatingUser ? 'Criando...' : 'Criar usuário'}
            </button>
          </form>
        </section>

        <section className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm min-h-[420px]">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">Usuários existentes</h2>
            <button
              type="button"
              onClick={() => loadUsers(false)}
              disabled={isLoadingUsers}
              className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-60"
            >
              Atualizar
            </button>
          </div>

          {isLoadingUsers && usuarios.length === 0 ? (
            <div className="flex items-center justify-center py-16">
              <div className="w-10 h-10 border-4 border-gray-200 border-t-blue-700 rounded-full animate-spin"></div>
            </div>
          ) : usuarios.length === 0 ? (
            <p className="text-sm text-gray-500">Nenhum usuário encontrado.</p>
          ) : (
            <div className="space-y-3">
              {usuarios.map((usuario) => (
                <div key={usuario.username} className="rounded-xl border border-gray-200 bg-gray-50 p-4">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <p className="text-sm font-semibold text-gray-900">{usuario.name || usuario.username}</p>
                    <span
                      className={`text-xs px-2 py-1 rounded-full font-semibold ${
                        usuario.enabled
                          ? 'bg-green-100 text-green-700'
                          : 'bg-gray-200 text-gray-600'
                      }`}
                    >
                      {usuario.enabled ? 'Ativo' : 'Inativo'}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 mt-1">{usuario.email}</p>
                  <p className="text-xs text-gray-500 mt-1">Status Cognito: {usuario.status}</p>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {(usuario.grupos || []).length > 0 ? (
                      usuario.grupos.map((grupo) => (
                        <span
                          key={`${usuario.username}-${grupo}`}
                          className="text-xs rounded-full bg-blue-100 px-2 py-1 font-semibold text-blue-700"
                        >
                          {grupo}
                        </span>
                      ))
                    ) : (
                      <span className="text-xs text-gray-500">Sem grupos</span>
                    )}
                  </div>
                </div>
              ))}

              {hasMore && (
                <div className="pt-2">
                  <button
                    type="button"
                    onClick={() => loadUsers(true, nextToken || undefined)}
                    disabled={isLoadingUsers || !nextToken}
                    className="rounded-lg border border-blue-700 px-4 py-2 text-sm font-semibold text-blue-700 hover:bg-blue-50 disabled:opacity-60"
                  >
                    {isLoadingUsers ? 'Carregando...' : 'Carregar mais'}
                  </button>
                </div>
              )}
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default AdminUsersPage;
